import { ColumnDef } from '@tanstack/react-table';
import { SetStateAction } from 'react';
import { Pagination } from '../../../../lib/interfaces/utils';
import { cn } from '../../../../lib/utils/cn';
import { formatDateTimeDE } from '../../../../lib/utils/date';
import { ArchiveFilter } from '../../constants/archive-filter.enum';
import { ProductAddOnListItemResponseDto } from '../../types/response';
import ArchiveBadge from '../archive-badge/archive-badge';
import ArchiveRowAction from '../archive-row-action/archive-row-action';
import DataTable from '../data-table/data-table';
import ListEmptyState from '../list-empty-state/list-empty-state';

const EMPTY_STATE: Record<ArchiveFilter, { title: string; description: string }> = {
  [ArchiveFilter.ACTIVE]: { title: 'Keine aktiven Add-ons', description: 'Lege ein Add-on an, um es in Angeboten zu verwenden.' },
  [ArchiveFilter.ARCHIVED]: { title: 'Keine archivierten Add-ons', description: 'Archivierte Add-ons erscheinen hier.' },
  [ArchiveFilter.ALL]: { title: 'Noch keine Add-ons', description: 'Lege dein erstes Add-on an, um loszulegen.' }
};

export interface AddOnListProps {
  addOns: ProductAddOnListItemResponseDto[];
  filter: ArchiveFilter;
  pagination: Pagination;
  pageCount: number;
  onPaginationChange: (next: SetStateAction<Pagination>) => void;
  isLoading: boolean;
  onEdit: (addOn: ProductAddOnListItemResponseDto) => void;
  onArchiveToggle: (addOn: ProductAddOnListItemResponseDto) => void;
}

const AddOnList = ({ addOns, filter, pagination, pageCount, onPaginationChange, isLoading, onEdit, onArchiveToggle }: AddOnListProps) => {
  const columns: ColumnDef<ProductAddOnListItemResponseDto>[] = [
    {
      id: 'title',
      header: 'Add-on',
      cell: ({ row }) => (
        <span className={cn('block truncate text-sm font-bold text-[#12282A]', row.original.isArchived && 'text-[#888]')} title={row.original.title}>
          {row.original.title}
        </span>
      )
    },
    {
      id: 'status',
      header: 'Status',
      size: 140,
      cell: ({ row }) => <ArchiveBadge isArchived={row.original.isArchived} />
    },
    {
      id: 'createdAt',
      header: 'Erstellt',
      size: 180,
      cell: ({ row }) => <span className="text-sm text-gray-700 whitespace-nowrap">{formatDateTimeDE(row.original.createdAt)}</span>
    },
    {
      id: 'actions',
      header: '',
      size: 80,
      cell: ({ row }) => (
        <div className="flex justify-end">
          <ArchiveRowAction isArchived={row.original.isArchived} onClick={() => onArchiveToggle(row.original)} />
        </div>
      )
    }
  ];

  return (
    <DataTable
      columns={columns}
      data={addOns}
      pagination={pagination}
      pageCount={pageCount}
      onPaginationChange={onPaginationChange}
      isLoading={isLoading}
      onRowClick={onEdit}
      emptyState={<ListEmptyState {...EMPTY_STATE[filter]} />}
    />
  );
};

export default AddOnList;
